/**
 * UI-3a Task 3 — CampaignGeneralTab.
 *
 * The "General" tab of the Campaign drawer (spec §3.4). Renders the campaign's
 * scalar settings: name, dialer mode, max CPS, max attempts/day, and the
 * optional calling-hours window.
 *
 * Public interface / how values reach the parent
 * -----------------------------------------------
 * This component OWNS its own `<ZodForm>` (per plan §0.H — Form* wrappers,
 * no raw useState for fields), mirroring the per-tab pattern used by
 * AgentsPage's "profile" tab.
 *
 *   - `defaultValues`  — current campaign values (omit for the create case).
 *   - `onSubmit`       — receives the validated `CampaignGeneralValues` after
 *                        the user submits. The parent `CampaignDrawer`
 *                        (Task 6) performs the actual API call here.
 *   - `formRef`        — optional ref forwarded to the underlying <form>, so a
 *                        drawer footer button can call
 *                        `formRef.current.requestSubmit()` to trigger this
 *                        tab's validated submit from outside.
 *   - `disabled`       — read-only rendering.
 *
 * HH:MM handling
 * --------------
 * The backend `callingHoursStart`/`callingHoursEnd` are nullable string fields
 * accepting `HH:MM` or `HH:MM:SS` (see apps/api/.../campaigns/schemas.ts).
 * PrimeReact's `Calendar` works with `Date` objects, so inside the form the
 * two calling-hours fields are held as `Date | null`. The Zod schema's OUTPUT
 * transforms each `Date` back to a zero-padded `"HH:MM"` string (or `null`
 * when empty — both empty means "24/7"). `onSubmit` therefore always receives
 * `string | null`, consistent with the backend contract.
 */
import { useMemo, type JSX } from 'react'
import { z } from 'zod'
import {
  ZodForm,
  FormText,
  FormDropdown,
  FormNumber,
  FormCalendar,
  FormSaveButton,
} from '../../forms/index.js'

/**
 * ITEM 63 — all SEVEN dialer modes the backend accepts.
 *
 * This offered three, lowercase. The backend has supported seven since Phase 5
 * and expects UPPERCASE:
 *
 *   API   routes/campaigns/schemas.ts:14  z.enum(['MANUAL','PREVIEW',…,'AMD'])
 *   DB    provision.ts:188                CHECK (dialer_type IN (…same seven…))
 *   Types packages/dialer/src/types.ts:1  DialerType
 *
 * So MANUAL, POWER, ROBO and AMD campaigns could not be created at all — four
 * engines that exist, are tested, and were unreachable from the product. And
 * the three that WERE offered used lowercase values the API rejects, so the
 * drawer's create/update call failed validation regardless (item 57).
 *
 * NOTE: contracts also exports `DialerTypeSchema` — four LOWERCASE values,
 * documented as "locked to Phase 1's lowercase wire format". That is the
 * frozen `dialer.originate` event format, NOT the campaign-creation contract.
 * Matching it here is the trap; the API enum is the one that governs.
 */
export const MODE_OPTIONS = [
  { label: 'Manual', value: 'MANUAL' },
  { label: 'Preview', value: 'PREVIEW' },
  { label: 'Progressive', value: 'PROGRESSIVE' },
  { label: 'Power', value: 'POWER' },
  { label: 'Predictive', value: 'PREDICTIVE' },
  { label: 'Robo', value: 'ROBO' },
  { label: 'AMD', value: 'AMD' },
] as const

export type CampaignMode =
  | 'MANUAL'
  | 'PREVIEW'
  | 'PROGRESSIVE'
  | 'POWER'
  | 'PREDICTIVE'
  | 'ROBO'
  | 'AMD'

/** Validated output emitted to the parent via `onSubmit`. */
export interface CampaignGeneralValues {
  name: string
  mode: CampaignMode
  maxCps: number
  maxAttemptsPerDay: number
  callingHoursStart: string | null
  callingHoursEnd: string | null
}

/** Inbound values for an existing campaign (calling hours as backend strings). */
export interface CampaignGeneralDefaults {
  name: string
  mode: CampaignMode
  maxCps: number
  maxAttemptsPerDay: number
  callingHoursStart: string | null
  callingHoursEnd: string | null
}

interface Props {
  /** Current values for an existing campaign; omit when creating. */
  defaultValues?: CampaignGeneralDefaults
  /** Called with validated values after the user submits the tab. */
  onSubmit: (values: CampaignGeneralValues) => Promise<void> | void
  /** Forwarded to the underlying <form> so a drawer can drive submission. */
  formRef?: React.RefObject<HTMLFormElement | null>
  disabled?: boolean
}

// --- HH:MM <-> Date conversion -------------------------------------------

/** Parse an `HH:MM`/`HH:MM:SS` string into a Date (today's date), else null. */
function timeStringToDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const m = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(value)
  if (!m) return null
  const hours = Number(m[1])
  const minutes = Number(m[2])
  if (hours > 23 || minutes > 59) return null
  const d = new Date()
  d.setHours(hours, minutes, 0, 0)
  return d
}

/** Format a Date as a zero-padded `HH:MM` string. */
function dateToTimeString(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

// --- Zod schema -----------------------------------------------------------

/**
 * A Calendar value: a valid Date, or null/undefined when empty. An invalid
 * Date (NaN time) is rejected with an HH:MM-format message.
 */
const callingHour = z
  .union([z.date(), z.null(), z.undefined()])
  .refine((v) => v == null || (v instanceof Date && !Number.isNaN(v.getTime())), {
    message: 'Must be a valid time (HH:MM)',
  })
  .transform((v): string | null => (v instanceof Date ? dateToTimeString(v) : null))

const CampaignGeneralSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(255, 'Name is too long'),
  // ITEM 63 — must match MODE_OPTIONS and the API enum
  // (routes/campaigns/schemas.ts:14). Was locked to three lowercase values.
  mode: z.enum(['MANUAL', 'PREVIEW', 'PROGRESSIVE', 'POWER', 'PREDICTIVE', 'ROBO', 'AMD']),
  maxCps: z
    .number({ message: 'Max CPS is required' })
    .int('Max CPS must be a whole number')
    .min(1, 'Max CPS must be between 1 and 1000')
    .max(1000, 'Max CPS must be between 1 and 1000'),
  maxAttemptsPerDay: z
    .number({ message: 'Max attempts is required' })
    .int('Max attempts must be a whole number')
    .min(1, 'Max attempts must be between 1 and 99')
    .max(99, 'Max attempts must be between 1 and 99'),
  callingHoursStart: callingHour,
  callingHoursEnd: callingHour,
})

/** Input shape the form holds before Zod transforms calling hours to strings. */
type CampaignGeneralInput = z.input<typeof CampaignGeneralSchema>

export function CampaignGeneralTab({
  defaultValues,
  onSubmit,
  formRef,
  disabled = false,
}: Props): JSX.Element {
  const formDefaults: CampaignGeneralInput = useMemo(
    () => ({
      name: defaultValues?.name ?? '',
      mode: defaultValues?.mode ?? 'PREVIEW',
      maxCps: defaultValues?.maxCps ?? 1,
      maxAttemptsPerDay: defaultValues?.maxAttemptsPerDay ?? 1,
      callingHoursStart: timeStringToDate(defaultValues?.callingHoursStart),
      callingHoursEnd: timeStringToDate(defaultValues?.callingHoursEnd),
    }),
    [defaultValues],
  )

  return (
    <ZodForm
      schema={CampaignGeneralSchema}
      defaultValues={formDefaults}
      onSubmit={(data) => onSubmit(data as CampaignGeneralValues)}
      {...(formRef ? { formRef } : {})}
    >
      <FormText
        name="name"
        label="Name"
        required
        autoFocus
        disabled={disabled}
        placeholder="Campaign name"
      />
      <FormDropdown
        name="mode"
        label="Mode"
        required
        disabled={disabled}
        options={MODE_OPTIONS.map((o) => ({ label: o.label, value: o.value }))}
        placeholder="Select a dialer mode…"
      />
      <FormNumber
        name="maxCps"
        label="Max CPS"
        required
        min={1}
        max={1000}
        disabled={disabled}
        helpText="Calls per second — between 1 and 1000."
      />
      <FormNumber
        name="maxAttemptsPerDay"
        label="Max attempts per day"
        required
        min={1}
        max={99}
        disabled={disabled}
        helpText="Per-contact dialing cap — between 1 and 99."
      />
      <FormCalendar
        name="callingHoursStart"
        label="Calling hours start"
        timeOnly
        hourFormat="24"
        disabled={disabled}
        placeholder="HH:MM"
        helpText="Leave both start and end empty for 24/7 dialing."
      />
      <FormCalendar
        name="callingHoursEnd"
        label="Calling hours end"
        timeOnly
        hourFormat="24"
        disabled={disabled}
        placeholder="HH:MM"
      />
      {!formRef && <FormSaveButton />}
    </ZodForm>
  )
}
